import deliverynoteService from '../../../services/deliverynoteService';
import filterService from '../../../services/filterService';

import {
    setDeliveryNoteLoading,
    setDeliveryNoteError,
    setDeliveryNoteList,
    setDeliveryNoteCurrent,
    setDeliveryNotePagination,
    setDeliveryNoteSort,
    setDeliveryNoteFilters,
    setDeliveryNoteDefaultFilter,
    setDeliveryNoteSettings,
    clearDeliveryNoteCurrent,
    clearDeliveryNoteError,
    setDeliveryNoteFilterLoading,
    changeDeliveryNoteFilterCondition,
    addDeliveryNoteFilterCondition,
    updateDeliveryNoteFilterConditionRule,
    updateDeliveryNoteFilterConditionValue,
    removeDeliveryNoteFilterCondition,
    setDeliveryNoteSelected,
    resetDeliveryNoteFiltersAndPagination
} from './deliverynoteSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().deliverynote.isSettingsInitialized) {
            dispatch(setDeliveryNoteLoading(true));
            const response = await deliverynoteService.getSettings();

            // Set the settings
            dispatch(setDeliveryNoteSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setDeliveryNoteDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setDeliveryNoteError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setDeliveryNoteLoading(false));
    }
};

export const fetchDeliverynotes = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().deliverynote;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setDeliveryNoteLoading(true));
        const response = await deliverynoteService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setDeliveryNoteList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchDeliverynotes:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setDeliveryNoteError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setDeliveryNoteLoading(false));
    }
};

export const fetchDeliveryNote = (id) => async (dispatch) => {
    try {
        dispatch(setDeliveryNoteLoading(true));
        const response = await deliverynoteService.getOne(id);
        dispatch(setDeliveryNoteCurrent(response.data));
    } catch (error) {
        dispatch(setDeliveryNoteError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setDeliveryNoteLoading(false));
    }
};

export const fetchDeliveryNoteGrouped = (id) => async (dispatch) => {
    try {
        dispatch(setDeliveryNoteLoading(true));
        const response = await deliverynoteService.getOneGrouped(id);
        dispatch(setDeliveryNoteCurrent(response.data));
        return response.data;
    } catch (error) {
        dispatch(setDeliveryNoteError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
        throw error;
    } finally {
        dispatch(setDeliveryNoteLoading(false));
    }
};

export const createDeliveryNote = (data) => async (dispatch) => {
    try {
        const response = await deliverynoteService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateDeliveryNote = (id, data) => async (dispatch) => {
    try {
        const response = await deliverynoteService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteDeliveryNote = (id) => async (dispatch) => {
    try {
        dispatch(setDeliveryNoteLoading(true));
        const response = await deliverynoteService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setDeliveryNoteError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setDeliveryNoteLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setDeliveryNotePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setDeliveryNoteSort(sort));
};

export const searchDeliverynotes = (query) => (dispatch, getState) => {
    const currentFilters = getState().deliverynote.filters;
    dispatch(setDeliveryNoteFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setDeliveryNoteDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeDeliveryNoteFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addDeliveryNoteFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateDeliveryNoteFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateDeliveryNoteFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeDeliveryNoteFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setDeliveryNoteFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().deliverynote.settings;
            dispatch(setDeliveryNoteSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setDeliveryNoteFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setDeliveryNoteFilterLoading(false));
        dispatch(setDeliveryNoteError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setDeliveryNoteSelected(selectedRows));
    };
};

export const clearCurrentDeliveryNote = () => {
    return (dispatch) => {
        dispatch(clearDeliveryNoteCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetDeliveryNoteFiltersAndPagination());
};